import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import PermissionLink from '../../components/PermissionLink';
import { listMaterials, listSkus, SkuRow } from '../../api/material';
import { ApiError } from '../../api/request';
import Pagination from '../../components/Pagination';
import SearchSelect from '../../components/SearchSelect';
import { i18nText } from '../../utils/i18n';
import { useSyncQueryToUrl } from '../../utils/urlState';

const PAGE_SIZE = 20;
const HAS_CMS_OPTS = [
    { value: 'yes', label: '已映射' },
    { value: 'no', label: '待映射' },
];

/** SKU 的主绑定材料编码 (多对多: 服务端主绑定在前); 未绑定 = ''. */
const primaryXcode = (s: SkuRow): string => s.materials?.[0]?.xcode || '';

// 三 ID 视图: 数据源 = /v1/skus, 按主绑定材料分组渲染 (组首行显示 xcode + ×N, 后续行 ↳).
// 全局仅三个标识: 材质编码 xcode → SKU-id → 耗材ID cms-ID; 2026-08-24 起 SKU↔材料多对多,
// 本页按主绑定归组 (额外绑定以 +N 提示), 完整绑定关系看 SKU 库.
function IDMappingList() {
    const location = useLocation();
    const initQ = new URLSearchParams(location.search);
    // 搜索: 关键词模糊 (sku_code/xcode/商品名) + 耗材ID精确 + 映射状态 (与 SkuList 同一套服务端过滤)
    const [input, setInput] = useState(initQ.get('keyword') || '');
    const [keyword, setKeyword] = useState(initQ.get('keyword') || '');
    const [cmsIdInput, setCmsIdInput] = useState(initQ.get('cmsId') || '');
    const [cmsId, setCmsId] = useState(initQ.get('cmsId') || '');
    const [hasCms, setHasCms] = useState(initQ.get('hasCms') || '');
    const [page, setPage] = useState(Number(initQ.get('page')) || 1);
    const [list, setList] = useState<SkuRow[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    // 顶部计数徽标: 材质编码总数 / SKU 总数 / 耗材ID 去重数
    const [materialCount, setMaterialCount] = useState<number | null>(null);
    const [skuCount, setSkuCount] = useState<number | null>(null); // 全局 SKU 数 (列表 total 受筛选影响, 不能用)
    const [cmsIdCount, setCmsIdCount] = useState<number | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await listSkus({
                keyword: keyword || undefined,
                cmsId: Number(cmsId) || undefined,
                hasCms: hasCms || undefined,
                page,
                pageSize: PAGE_SIZE,
            });
            // 按主绑定材料排序保证同材质的 SKU 相邻, 分组渲染才成立 (多对多: 取主绑定, 未绑定归 '' 组)
            const rows = [...(res.list || [])].sort((a, b) =>
                primaryXcode(a).localeCompare(primaryXcode(b)),
            );
            setList(rows);
            setTotal(res.totalCount);
        } catch (e) {
            const msg =
                e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
            setError(msg);
            setList([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }, [keyword, cmsId, hasCms, page]);

    useEffect(() => {
        load();
    }, [load]);

    // 页面挂在 /id-mappings 与 /materials/id-mapping 两个路由下, path 用当前值, 不能写死
    useSyncQueryToUrl(location.pathname, { keyword, cmsId, hasCms, page });

    const onSearch = (e: React.FormEvent) => {
        e.preventDefault();
        const nextKw = input.trim();
        const nextCms = cmsIdInput.trim();
        if (nextKw === keyword && nextCms === cmsId && page === 1) {
            load();
            return;
        }
        setKeyword(nextKw);
        setCmsId(nextCms);
        setPage(1);
    };

    // 统计只在进入页面时拉一次: 材料总数 (pageSize=1 拿 totalCount) + 全量 SKU 的耗材ID去重
    useEffect(() => {
        let cancelled = false;
        listMaterials({ page: 1, pageSize: 1 })
            .then((res) => {
                if (!cancelled) setMaterialCount(res.totalCount);
            })
            .catch(() => {
                if (!cancelled) setMaterialCount(null);
            });
        (async () => {
            try {
                const ids = new Set<number>();
                let p = 1;
                let seen = 0;
                for (;;) {
                    const res = await listSkus({ page: p, pageSize: 500 });
                    if (p === 1 && !cancelled) setSkuCount(res.totalCount);
                    const batch = res.list || [];
                    for (const s of batch) {
                        if (s.cmsId > 0) ids.add(s.cmsId);
                    }
                    seen += batch.length;
                    if (batch.length === 0 || seen >= res.totalCount) break;
                    p += 1;
                }
                if (!cancelled) setCmsIdCount(ids.size);
            } catch {
                if (!cancelled) setCmsIdCount(null);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    // 当前页内按主绑定材料分组: 每组第一行展示 xcode 链接 + ×N
    const groupSize: Record<string, number> = {};
    for (const s of list)
        groupSize[primaryXcode(s)] = (groupSize[primaryXcode(s)] || 0) + 1;

    let prevXcode = '__init__';

    return (
        <div className="space-y-5">
            <div className="flex items-center justify-between gap-4">
                <div>
                    <div className="text-base font-semibold">ID 映射治理</div>
                    <div className="text-xs text-slate-500">
                        全局仅三个标识: <b>材质编码 xcode</b>(材质级) →{' '}
                        <b>SKU-id</b>(一物一码) → <b>耗材ID cms-ID</b>(映射
                        CMS)。关系 <b>xcode N:M SKU-id 1:1 耗材ID</b>
                        (2026-08-24 起多对多, 本页按主绑定归组); CMS
                        后台清洗后实现 SKU ↔ 耗材ID 一一对应。
                    </div>
                </div>
                <form
                    onSubmit={onSearch}
                    className="flex gap-2 items-center shrink-0"
                >
                    <SearchSelect
                        className="w-28"
                        value={hasCms}
                        onChange={(v) => {
                            setHasCms(v);
                            setPage(1);
                        }}
                        placeholder="全部状态"
                        options={HAS_CMS_OPTS}
                        clearable
                    />
                    <input
                        className="inp w-28"
                        placeholder="耗材ID"
                        inputMode="numeric"
                        value={cmsIdInput}
                        onChange={(e) =>
                            setCmsIdInput(e.target.value.replace(/\D/g, ''))
                        }
                        onBlur={() => {
                            if (cmsIdInput.trim() === '' && cmsId !== '') {
                                setCmsId('');
                                setPage(1);
                            }
                        }}
                    />
                    <input
                        className="inp w-64"
                        placeholder="搜索 材质编码 / SKU编码 / 材质名"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onBlur={() => {
                            if (input.trim() === '' && keyword !== '') {
                                setKeyword('');
                                setPage(1);
                            }
                        }}
                    />
                    <button type="submit" className="btn-ghost">
                        查询
                    </button>
                </form>
            </div>

            <div className="flex flex-wrap gap-2 text-xs">
                <span className="tag bg-brand-50 text-brand-700">
                    材质编码 xcode · {materialCount ?? '…'}
                </span>
                <span className="tag bg-slate-100 text-slate-600">
                    SKU-id · {skuCount ?? '…'}
                </span>
                <span className="tag bg-emerald-50 text-emerald-700">
                    耗材ID cms-ID · {cmsIdCount ?? '…'}
                </span>
            </div>

            {error && (
                <div className="card p-3 text-sm text-rose-600 border-rose-200 bg-rose-50">
                    错误: {error}
                </div>
            )}

            <div className="card overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-slate-50">
                        <tr>
                            <th className="th">材质编码 xcode</th>
                            <th className="th">材质名称</th>
                            <th className="th">SKU-id</th>
                            <th className="th">耗材ID cms-ID</th>
                            <th className="th">映射状态</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr>
                                <td
                                    className="td text-center text-slate-400"
                                    colSpan={5}
                                >
                                    加载中…
                                </td>
                            </tr>
                        )}
                        {!loading && list.length === 0 && (
                            <tr>
                                <td
                                    className="td text-center text-slate-400"
                                    colSpan={5}
                                >
                                    {keyword || cmsId || hasCms
                                        ? '该筛选下暂无数据'
                                        : '暂无数据'}
                                </td>
                            </tr>
                        )}
                        {!loading &&
                            list.map((s) => {
                                const px = primaryXcode(s);
                                const isFirst = px !== prevXcode;
                                prevXcode = px;
                                const extra = (s.materials || []).length - 1;
                                return (
                                    <tr
                                        key={s.id}
                                        className="hover:bg-slate-50"
                                    >
                                        <td className="td font-mono text-xs">
                                            {isFirst ? (
                                                px ? (
                                                    <>
                                                        <PermissionLink
                                                            resource="material"
                                                            to={`/materials/${encodeURIComponent(px)}`}
                                                            className="text-brand-600 hover:underline"
                                                        >
                                                            {px}
                                                        </PermissionLink>{' '}
                                                        <span className="text-slate-400">
                                                            ×{groupSize[px]}
                                                        </span>
                                                    </>
                                                ) : (
                                                    <span className="tag bg-amber-50 text-amber-700">
                                                        未绑定 ×{groupSize['']}
                                                    </span>
                                                )
                                            ) : (
                                                <span className="text-slate-300">
                                                    ↳
                                                </span>
                                            )}
                                        </td>
                                        <td className="td text-xs">
                                            {isFirst && px
                                                ? i18nText(
                                                      s.materials?.[0]?.name ||
                                                          {},
                                                  )
                                                : ''}
                                        </td>
                                        <td className="td font-mono text-xs">
                                            {s.skuCode}
                                            {extra > 0 && (
                                                <span
                                                    className="ml-1 text-[10px] text-slate-400"
                                                    title={`另绑定 ${extra} 个材料: ${(
                                                        s.materials || []
                                                    )
                                                        .slice(1)
                                                        .map((m) => m.xcode)
                                                        .join('、')}`}
                                                >
                                                    +{extra}
                                                </span>
                                            )}
                                        </td>
                                        <td className="td font-mono text-xs">
                                            {s.cmsId > 0 ? s.cmsId : '—'}
                                        </td>
                                        <td className="td">
                                            {s.cmsId > 0 ? (
                                                <span className="tag cap-on">
                                                    ✓ 已映射 CMS
                                                </span>
                                            ) : (
                                                <span className="tag bg-amber-50 text-amber-700">
                                                    待映射
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                    </tbody>
                </table>
                <div className="text-[11px] text-slate-400 p-3">
                    材质编码与 SKU-id 多对多 (本页按主绑定归组, +N
                    表示额外绑定), 每个 SKU-id 各自映射一个耗材ID;
                    完整绑定关系见{' '}
                    <PermissionLink
                        resource="material"
                        to="/skus"
                        className="text-brand-600 hover:underline"
                    >
                        SKU 库 →
                    </PermissionLink>
                </div>
            </div>

            <div className="flex items-center justify-between text-xs text-slate-500">
                <span>共 {total} 条</span>
                <div className="flex items-center gap-2">
                    <Pagination
                        page={page}
                        totalPages={totalPages}
                        onChange={setPage}
                    />
                </div>
            </div>
        </div>
    );
}

export default IDMappingList;
